// Stadtteil-Grenzen aus GeoJSON als Sektoren uebernehmen.
//
// Sektoren sind fast immer Stadtteile, und Staedte veroeffentlichen deren
// Grenzen als offene Daten — ueber eine ArcGIS-/WFS-Abfrage oder als Datei im
// Geoportal. Nachzeichnen von Hand ist dagegen Fleissarbeit mit schlechterem
// Ergebnis.
//
// Zwei Dinge muessen dieser Import zwingend leisten:
//
//  1. VEREINFACHEN. Amtliche Grenzen haben mehrere hundert bis ueber tausend
//     Stuetzpunkte (Kassel: 171–859 je Ortsbezirk). Der Karteneditor zeichnet
//     je Punkt einen nummerierten Marker, und jede Fahrerposition wird gegen
//     jeden Sektor geprueft. 25 m Toleranz druecken Kassel auf 18–127 Punkte
//     bei unter 0,7 % Flaechenabweichung.
//  2. AUF EINEN RING REDUZIEREN. Das Sektor-Modell kennt genau einen
//     geschlossenen Ring — keine Exklaven, keine Loecher. Beides kommt in
//     echten Daten vor und muss sichtbar gemeldet statt still geschluckt
//     werden.

import * as polygonClipping from 'polygon-clipping';
import { hasSelfIntersection, repairSelfIntersections } from './sectorGeometry';

const METERS_PER_DEGREE = 111320;
const POLYGON_TYPES = ['Polygon', 'MultiPolygon'];
// Vereinigte Grenzen aus verschiedenen Quellen lassen an ihren Beruehrungen
// Zwickel von wenigen hundert Quadratmetern zurueck. Als „Loch" gemeldet wird
// nur, was gross genug ist, um eine bewusste Aussparung zu sein.
const MIN_REPORTED_HOLE_SQM = 10000;

export const SIMPLIFY_LEVELS = [
  { key: 'fein', label: 'Fein — 10 m', toleranceMeters: 10 },
  { key: 'mittel', label: 'Mittel — 25 m', toleranceMeters: 25 },
  { key: 'grob', label: 'Grob — 50 m', toleranceMeters: 50 },
  { key: 'roh', label: 'Unverändert — alle Punkte', toleranceMeters: 0 }
];

export const DEFAULT_SIMPLIFY_KEY = 'mittel';

export const toleranceForLevel = (key) =>
  (SIMPLIFY_LEVELS.find(level => level.key === key) || SIMPLIFY_LEVELS[1]).toleranceMeters;

// ---------------------------------------------------------------------------
// URL-Aufbereitung
// ---------------------------------------------------------------------------

const ARCGIS_LAYER = /\/(?:MapServer|FeatureServer)\/\d+/i;

/**
 * Ergaenzt an einer ArcGIS-Layer-URL die Abfrageparameter, die man sonst von
 * Hand anhaengen muesste. Wer im Geoportal auf einen Layer klickt, hat genau
 * die URL in der Adresszeile, die hier hereinkommt — ohne `/query` und ohne
 * `f=geojson` liefert sie eine HTML-Seite statt Daten.
 *
 * Gibt die unveraenderte URL zurueck, wenn es keine ArcGIS-URL ist (WFS,
 * direkter Dateilink), und dazu die vorgenommenen Ergaenzungen als Klartext.
 */
export const normalizeGeoJsonUrl = (rawUrl) => {
  const trimmed = (rawUrl || '').trim();
  if (!trimmed) return { url: '', hints: [] };

  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch (error) {
    return { url: trimmed, hints: [] };
  }
  if (!ARCGIS_LAYER.test(parsed.pathname)) return { url: trimmed, hints: [] };

  const hints = [];
  if (!/\/query$/i.test(parsed.pathname)) {
    parsed.pathname = `${parsed.pathname.replace(/\/+$/, '')}/query`;
    hints.push('„/query" ergänzt');
  }

  const params = parsed.searchParams;
  const ensure = (key, value, hint) => {
    if (params.get(key) !== value) {
      params.set(key, value);
      hints.push(hint);
    }
  };
  ensure('f', 'geojson', 'Ausgabeformat auf „f=geojson" gesetzt');
  // Ohne outSR=4326 liefern die meisten Portale ihr lokales System (z. B. UTM
  // 32N) — die Zahlen sehen dann aus wie 32500000 statt 9.49.
  ensure('outSR', '4326', 'Koordinatensystem auf „outSR=4326" (WGS84) gesetzt');
  ensure('returnGeometry', 'true', '„returnGeometry=true" gesetzt');
  if (!params.get('where')) {
    params.set('where', '1=1');
    hints.push('„where=1=1" ergänzt (alle Datensätze)');
  }
  if (!params.get('outFields')) {
    params.set('outFields', '*');
    hints.push('„outFields=*" ergänzt (alle Attribute)');
  }

  return { url: parsed.toString(), hints };
};

/**
 * Zerlegt eine ArcGIS-Layer-URL in Dienst und Ebenen-Nummer.
 *
 * Damit lassen sich die uebrigen Ebenen desselben Dienstes anbieten: wer die
 * Stadtteile geladen hat, findet die Gemeindegrenze fuer das Betriebsgebiet im
 * selben Server, nur unter einer anderen Nummer.
 */
export const arcGisLayerInfo = (rawUrl) => {
  let parsed;
  try {
    parsed = new URL((rawUrl || '').trim());
  } catch (error) {
    return null;
  }
  const match = parsed.pathname.match(/^(.*\/(?:MapServer|FeatureServer))\/(\d+)(?:\/query)?\/?$/i);
  if (!match) return null;
  return {
    serviceUrl: `${parsed.origin}${match[1]}`,
    layerId: Number(match[2])
  };
};

/// Ebenen eines ArcGIS-Dienstes; Gruppen- und Nicht-Flaechen-Ebenen fallen raus,
/// weil sich daraus weder Sektor noch Betriebsgebiet bauen laesst.
export const readServiceLayers = (serviceInfo) => {
  const layers = Array.isArray(serviceInfo?.layers) ? serviceInfo.layers : [];
  return layers
    .filter(layer => layer?.geometryType === 'esriGeometryPolygon')
    .map(layer => ({ id: layer.id, name: layer.name || `Ebene ${layer.id}` }));
};

// ---------------------------------------------------------------------------
// GeoJSON lesen
// ---------------------------------------------------------------------------

export const readFeatures = (json) => {
  if (!json || typeof json !== 'object') {
    throw new Error('Die Antwort enthält kein GeoJSON.');
  }
  // ArcGIS antwortet ohne f=geojson im eigenen Format (attributes/rings).
  // Das ist der haeufigste Fehlgriff, deshalb eine eigene Meldung.
  const firstFeature = Array.isArray(json.features) ? json.features[0] : null;
  if (json.geometryType || (firstFeature && firstFeature.attributes)) {
    throw new Error('Die Antwort ist ArcGIS-JSON statt GeoJSON. Bitte „f=geojson" an die URL anhängen.');
  }
  if (json.type === 'FeatureCollection') {
    return Array.isArray(json.features) ? json.features : [];
  }
  if (json.type === 'Feature') return [json];
  if (json.type === 'GeometryCollection') {
    return (json.geometries || []).map(geometry => ({ type: 'Feature', properties: {}, geometry }));
  }
  if (json.type === 'Polygon' || json.type === 'MultiPolygon') {
    return [{ type: 'Feature', properties: {}, geometry: json }];
  }
  throw new Error('Unbekanntes Format — erwartet wird eine GeoJSON-FeatureCollection.');
};

export const collectPropertyKeys = (features) => {
  const keys = [];
  (features || []).forEach(feature => {
    Object.keys(feature?.properties || {}).forEach(key => {
      if (!keys.includes(key)) keys.push(key);
    });
  });
  return keys;
};

// ArcGIS-Verwaltungsspalten; als Name oder Nummer taugen sie nicht, auswaehlbar
// bleiben sie trotzdem.
const TECHNICAL_KEY = /^(objectid|fid|globalid|shape([_.]?(area|length|leng))?|se_anno_cad_data)$/i;

const NAME_HINTS = ['name', 'bezeichnung', 'stadtteil', 'ortsteil', 'ortsbezirk', 'bezirk', 'quartier', 'gebiet', 'titel', 'label'];
const CODE_HINTS = ['nr', 'nummer', 'num', 'code', 'schluessel', 'schlüssel', 'kennzahl', 'kuerzel', 'kürzel', 'key', 'obz', 'id'];

const scoreKey = (key, hints) => {
  const lower = key.toLowerCase();
  if (TECHNICAL_KEY.test(lower)) return -1;
  let best = -1;
  hints.forEach((hint, index) => {
    if (lower === hint) best = Math.max(best, 100 - index);
    else if (lower.includes(hint)) best = Math.max(best, 50 - index);
  });
  return best;
};

const pickKey = (keys, hints, exclude) => {
  let winner = '';
  let winnerScore = 0;
  (keys || []).forEach(key => {
    if (key === exclude) return;
    const score = scoreKey(key, hints);
    if (score > winnerScore) {
      winner = key;
      winnerScore = score;
    }
  });
  return winner;
};

/**
 * Raet, welche Spalte Name und welche Sektornummer ist. Bei Kassel sind das
 * `OBZ_Name` und `OBZ`; verlassen wird sich darauf nicht — beides bleibt im
 * Dialog umstellbar.
 */
export const guessPropertyKeys = (features) => {
  const keys = collectPropertyKeys(features);
  const nameKey = pickKey(keys, NAME_HINTS, null);
  const codeKey = pickKey(keys, CODE_HINTS, nameKey);
  return { keys, nameKey, codeKey };
};

// ---------------------------------------------------------------------------
// Geometrie
// ---------------------------------------------------------------------------

/// Aussen- und Innenringe getrennt. Loecher kann das Sektor-Modell nicht
/// abbilden; sie werden nur gemeldet, wenn sie ins Gewicht fallen.
export const outerRingsOf = (geometry) => {
  if (!geometry) return { rings: [], holes: [] };
  if (geometry.type === 'Polygon') {
    const rings = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
    return { rings: rings.slice(0, 1), holes: rings.slice(1) };
  }
  if (geometry.type === 'MultiPolygon') {
    const polygons = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
    const rings = [];
    const holes = [];
    polygons.forEach(polygon => {
      if (!Array.isArray(polygon) || !polygon.length) return;
      rings.push(polygon[0]);
      polygon.slice(1).forEach(hole => holes.push(hole));
    });
    return { rings, holes };
  }
  return { rings: [], holes: [] };
};

/// GeoJSON fuehrt [Laenge, Breite], unser Modell {lat, lng}. Der schliessende
/// Wiederholungspunkt faellt weg, weil unsere Polygone implizit geschlossen sind.
export const ringToLatLng = (ring) => {
  const points = (ring || [])
    .filter(pair => Array.isArray(pair) && pair.length >= 2)
    .map(pair => ({ lat: Number(pair[1]), lng: Number(pair[0]) }));
  if (points.length > 1) {
    const first = points[0];
    const last = points[points.length - 1];
    if (first.lat === last.lat && first.lng === last.lng) points.pop();
  }
  return points;
};

export const isWgs84Ring = (points) => (points || []).every(point => (
  Number.isFinite(point.lat) && Number.isFinite(point.lng) &&
  Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180
));

/// Grobe Flaeche in m², nur um den groessten Ring zu bestimmen.
export const ringAreaSquareMeters = (points) => {
  if (!points || points.length < 3) return 0;
  const latMean = points.reduce((sum, point) => sum + point.lat, 0) / points.length;
  const kx = Math.cos((latMean * Math.PI) / 180);
  let sum = 0;
  for (let i = 0; i < points.length; i += 1) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    sum += (current.lng * kx) * next.lat - (next.lng * kx) * current.lat;
  }
  return Math.abs(sum / 2) * METERS_PER_DEGREE * METERS_PER_DEGREE;
};

/**
 * Douglas-Peucker mit Toleranz in Metern.
 *
 * Iterativ statt rekursiv: bei mehreren tausend Stuetzpunkten waere die
 * Rekursionstiefe im schlechtesten Fall so gross wie die Punktzahl.
 */
export const simplifyRing = (points, toleranceMeters) => {
  if (!toleranceMeters || !points || points.length <= 4) return points || [];

  const latMean = points.reduce((sum, point) => sum + point.lat, 0) / points.length;
  const kx = Math.cos((latMean * Math.PI) / 180) * METERS_PER_DEGREE;
  const projected = points.map(point => ({ x: point.lng * kx, y: point.lat * METERS_PER_DEGREE }));

  const keep = new Array(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;

  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [first, last] = stack.pop();
    if (last <= first + 1) continue;

    const start = projected[first];
    const end = projected[last];
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSquared = dx * dx + dy * dy;

    let maxDistance = 0;
    let farthest = -1;
    for (let i = first + 1; i < last; i += 1) {
      const point = projected[i];
      let distance;
      if (lengthSquared === 0) {
        distance = Math.hypot(point.x - start.x, point.y - start.y);
      } else {
        const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
        distance = Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy));
      }
      if (distance > maxDistance) {
        maxDistance = distance;
        farthest = i;
      }
    }

    if (maxDistance > toleranceMeters && farthest > first) {
      keep[farthest] = true;
      stack.push([first, farthest], [farthest, last]);
    }
  }

  const simplified = points.filter((_, index) => keep[index]);
  // Lieber unvereinfacht als kein Polygon mehr.
  return simplified.length >= 3 ? simplified : points;
};

/**
 * Wie `simplifyRing`, gibt aber nie ein Polygon zurueck, das sich selbst
 * kreuzt.
 *
 * Douglas-Peucker kann an engen Schleifen eine Kreuzung erzeugen, die es im
 * Rohpolygon nicht gibt (bei den Kasseler Gemeindegrenzen trifft das 4 von 39).
 * Statt solche Flaechen zu sperren, wird die Toleranz halbiert, bis das
 * Ergebnis sauber ist — im Zweifel bleibt das unveraenderte Polygon.
 */
export const simplifyRingSafely = (points, toleranceMeters) => {
  if (!toleranceMeters || !points || points.length <= 4) return points || [];
  let tolerance = toleranceMeters;
  for (let attempt = 0; attempt < 4 && tolerance >= 1; attempt += 1) {
    const simplified = simplifyRing(points, tolerance);
    if (!hasSelfIntersection(simplified)) return simplified;
    // Meist ist es eine einzige enge Stelle; die kostet ein paar Punkte,
    // nicht die ganze Vereinfachung.
    const repaired = repairSelfIntersections(simplified);
    if (repaired.length >= 3 && !hasSelfIntersection(repaired)) return repaired;
    tolerance = Math.round(tolerance / 2);
  }
  return points;
};

/**
 * Vereinigt mehrere Flaechen zu einer (Dissolve) — der Aussenriss mehrerer
 * Stadtteile oder Gemeinden als ein Betriebsgebiet.
 *
 * Vereinigt werden bewusst die ROHEN Geometrien; vereinfacht wird erst
 * danach einmal ueber das Ergebnis. Andersherum driften die gemeinsamen
 * Grenzen beim Vereinfachen auseinander und die Vereinigung laesst Splitter
 * und Loecher zurueck.
 */
export const unionGeometries = (geometries) => {
  const usable = (geometries || []).filter(geometry =>
    geometry && POLYGON_TYPES.includes(geometry.type) && Array.isArray(geometry.coordinates));
  if (!usable.length) return null;
  if (usable.length === 1) return usable[0];

  const shapes = usable.map(geometry => geometry.coordinates);
  let merged;
  try {
    merged = polygonClipping.union(shapes[0], ...shapes.slice(1));
  } catch (error) {
    throw new Error('Die Flächen konnten nicht vereinigt werden. Bitte einzeln übernehmen.');
  }
  if (!Array.isArray(merged) || !merged.length) {
    throw new Error('Die Vereinigung ergab keine Fläche.');
  }
  return { type: 'MultiPolygon', coordinates: merged };
};

// ---------------------------------------------------------------------------
// Import-Kandidaten
// ---------------------------------------------------------------------------

const readProperty = (properties, key) => {
  if (!key) return '';
  const value = properties?.[key];
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

/**
 * Baut je Feature einen Vorschlag fuer einen Sektor. Der Dialog zeigt die
 * Liste, der Benutzer waehlt aus und kann Name und Nummer noch korrigieren.
 */
export const buildImportCandidates = (features, options = {}) => {
  const { nameKey, codeKey, toleranceMeters = 0 } = options;

  return (features || []).map((feature, index) => {
    const properties = feature?.properties || {};
    const geometry = feature?.geometry;
    const unsupportedGeometry = !geometry || !POLYGON_TYPES.includes(geometry.type);

    const { rings, holes } = outerRingsOf(geometry);
    const converted = rings.map(ringToLatLng).filter(ring => ring.length >= 3);
    const usable = converted.filter(isWgs84Ring);
    const invalidCoordinates = converted.length > 0 && usable.length === 0;

    const ranked = usable
      .map(ring => ({ ring, area: ringAreaSquareMeters(ring) }))
      .sort((a, b) => b.area - a.area);
    const largest = ranked[0];
    const holeCount = holes
      .map(ring => ringAreaSquareMeters(ringToLatLng(ring)))
      .filter(area => area >= MIN_REPORTED_HOLE_SQM).length;
    const points = largest ? simplifyRingSafely(largest.ring, toleranceMeters) : [];

    return {
      key: `feature-${index}`,
      name: readProperty(properties, nameKey),
      code: readProperty(properties, codeKey),
      points,
      rawPointCount: largest ? largest.ring.length : 0,
      pointCount: points.length,
      // Exklaven: unser Modell kennt einen Ring, der groesste gewinnt.
      skippedRingCount: Math.max(0, ranked.length - 1),
      holeCount,
      invalidCoordinates,
      unsupportedGeometry,
      geometryType: geometry?.type || 'unbekannt',
      selfIntersecting: points.length >= 4 ? hasSelfIntersection(points) : false,
      // Rohgeometrie, damit die Vereinigung nicht auf den vereinfachten
      // Umrissen arbeiten muss.
      geometry,
      properties
    };
  });
};

/// Nicht importierbar — der Dialog blendet solche Zeilen aus der Auswahl aus.
export const candidateBlockReason = (candidate) => {
  if (!candidate) return 'Unbekannt';
  if (candidate.unsupportedGeometry) return `Keine Fläche (${candidate.geometryType})`;
  if (candidate.invalidCoordinates) return 'Nicht WGS84 — „outSR=4326" fehlt';
  if (candidate.pointCount < 3) return 'Weniger als 3 Punkte';
  if (candidate.selfIntersecting) return 'Polygon überschneidet sich selbst';
  return null;
};

// ---------------------------------------------------------------------------
// Namenssuche (OpenStreetMap / Nominatim)
// ---------------------------------------------------------------------------
//
// Fuer Staedte ohne Geoportal. Drei Bedingungen aus den Nutzungsbedingungen
// (operations.osmfoundation.org/policies/nominatim) sind bindend:
//
//   - KEIN Type-ahead. „You must not implement such a service on the client
//     side using the API." Deshalb Suchfeld plus Knopf, eine Abfrage pro Klick.
//   - Hoechstens eine Abfrage pro Sekunde.
//   - Ergebnisse muessen zwischengespeichert werden; dieselbe Abfrage darf
//     nicht wiederholt rausgehen.
//
// Der User-Agent laesst sich aus dem Browser nicht setzen; die Bedingungen
// akzeptieren alternativ einen aussagekraeftigen Referer, und den schickt der
// Browser mit der Portal-Domain automatisch.
//
// Lizenz: ODbL. Wo Suchergebnisse angezeigt werden, gehoert die Namensnennung
// „© OpenStreetMap-Mitwirkende" dazu.

const NOMINATIM_SEARCH = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_MIN_INTERVAL_MS = 1100;

const searchCache = new Map();
let lastSearchAt = 0;

export const buildNominatimUrl = (query) => {
  const params = new URLSearchParams({
    q: (query || '').trim(),
    format: 'jsonv2',
    polygon_geojson: '1',
    addressdetails: '1',
    limit: '10',
    'accept-language': 'de'
  });
  return `${NOMINATIM_SEARCH}?${params.toString()}`;
};

/// Nominatim-Treffer in dieselbe Feature-Form bringen, die auch aus einer
/// GeoJSON-Quelle kommt — damit ist der restliche Ablauf identisch.
export const readNominatimResults = (results) => (Array.isArray(results) ? results : []).map(result => {
  const displayName = result?.display_name || '';
  return {
    type: 'Feature',
    properties: {
      name: result?.name || displayName.split(',')[0] || '',
      art: [result?.category || result?.class, result?.type].filter(Boolean).join('/'),
      adresse: displayName
    },
    geometry: result?.geojson || null
  };
});

/**
 * Eine Suche, mit Zwischenspeicher und Mindestabstand.
 *
 * `fetchJson` ist herausgezogen, damit der Test nicht ins Netz geht.
 */
export const searchPlaces = async (query, fetchJson) => {
  const trimmed = (query || '').trim();
  if (trimmed.length < 3) throw new Error('Bitte mindestens drei Zeichen eingeben.');

  const url = buildNominatimUrl(trimmed);
  if (searchCache.has(url)) return searchCache.get(url);

  const wait = NOMINATIM_MIN_INTERVAL_MS - (Date.now() - lastSearchAt);
  if (wait > 0) await new Promise(resolve => setTimeout(resolve, wait));
  lastSearchAt = Date.now();

  const features = readNominatimResults(await fetchJson(url));
  searchCache.set(url, features);
  return features;
};
